import { useTranslation } from 'react-i18next';
import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'clsx';
import { Modal, UserAvatar } from '@/components';
import { useCurrentAddressInfo, useCurrentUserPermission, useTheme } from '@/hooks';
import { EditorSubmitContentData } from '@/components';
import { RichTextCommentEditor } from '@debank/content-editor';
import { FeedComment } from '../../types';
import { useFeedCommentAdd } from '../../hooks';
import style from './index.module.scss';
import { initEditorDependency } from '@/views/CreateStream/initEditorDependency';
import { PermissionBanner } from '@/views/CreateStream/components/PermissionBanner';

const { CommonModal } = Modal;

export const ArticleCreateCommentModal = ({
  articleId,
  replyTo,
  contributorId,
  on,
  turnOff,
}: {
  replyTo?: FeedComment;
  articleId: number;
  contributorId?: string;
  on?: boolean;
  turnOff?: () => void;
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    initEditorDependency();
  }, []);

  const { mutate } = useFeedCommentAdd(articleId);
  const { detail, userInfo } = useCurrentAddressInfo();
  const userDetail = useMemo(() => userInfo || detail, [detail, userInfo]);
  const { isDark } = useTheme();
  const { data: commentPermission } = useCurrentUserPermission({
    operate_type: 'feed_comment',
    operate_args: {
      article_id: articleId,
    },
    enabled: !!on,
  });

  const turnOffModal = useCallback(() => {
    turnOff && turnOff();
  }, [turnOff]);

  const handlePostClick = async (data: EditorSubmitContentData) => {
    mutate(
      {
        content: data.content,
        image_ids: data.image_ids,
        entities: data.entities,
        reply_to_comment_id: replyTo?.id,
        contributor_id: contributorId,
      },
      {
        onSuccess: turnOffModal,
      },
    );
  };

  useEffect(() => {
    if (on) {
      inputRef.current?.focus();
    }
  }, [on]);

  return (
    <CommonModal on={on} containerClassName={style.commentModal} turnOff={turnOffModal}>
      <PermissionBanner
        inComment
        permissionData={commentPermission}
        currentUserInfo={userInfo}
        setDisabled={setDisabled}
        onClickMint={() => turnOffModal()}
      />
      {replyTo?._author ? (
        <div className={style.replyTo}>
          {t('ArticleCreateCommentModal.yslhab')}
          <span className={style.replyToId}>@{replyTo._author}</span>
        </div>
      ) : null}
      <div className={style.commentBody}>
        <UserAvatar className={style.avatar} size={48} data={userDetail?.desc} />

        <RichTextCommentEditor
          placeholderClassName={style.placeholderClassName}
          placeholder={t('ArticleCreateCommentModal.addComment')}
          onConfirm={handlePostClick}
          autoFocus
          maxLength={300}
          isDarkMode={isDark}
          editorClassName={cx(style.editorClassName)}
          disabled={disabled}
        />
      </div>
    </CommonModal>
  );
};
